<template>
<!-- eslint-disable max-len -->
<span>
  <top-bar v-if="narrative.showNavbar" :show-player="showName && narrative.leftPlayer" :show-right-player="showName && narrative.rightPlayer">
    <template :slot="narrative.showMetrics ? 'challenge' : 'default'">
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks
        This is what the following line used to be:
        {{ narrativeNavbarText }}
      -->
      <p v-html="narrativeNavbarText"></p>
    </template>
    <cash-navbar v-if="narrative.showMetrics" slot="cash"></cash-navbar>
    <metrics-navbar v-if="narrative.showMetrics" slot="metrics"></metrics-navbar>
  </top-bar>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <narrator-bubble
      v-if="showSpeakerBubble && ['left', 'right', 'extra-left', 'extra-right'].includes(narrative.speaker)"
      :left="bubbleLeft"
      :thought="narrative.thoughtBubble"
      class="narrator-bubble"
      :class="{
        'with-bars': narrative.showNavbar,
        left: (narrative.speaker === 'left' || narrative.speaker === 'extra-left'),
        native
      }"
      :width="`${(starfighter) ? '850px' : '1400px'}`"
      :style="bubbleStyle"
      tooltip-target="speechBubble"
      narrative
    >
      <div
        :style="dialogStyle"
      >
        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks,
          but this is okay here since narrativeText is only used/set internally.
          AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
          This is what the following line used to be:
          <paragraphs>{{ narrativeText }}</paragraphs>
        -->
        <p v-html="narrativeText"></p>
        <div
          v-if="!useMediaZoneForNarrative && narrative.images && narrative.images.length"
          style="width: 100%; display: flex; flex-direction: row; justify-content: space-around; align-items: center;"
        >
          <div
            v-for="(image, i) in narrative.images"
            :key="`ni-${i}`"
            :style="`width: ${96 / narrative.images.length}%;`"
            class="narrative-image"
            @click="expandImage(image)"
          >
            <img
              style="max-width: 100%; object-fit: contain;"
              :src="resolveAssetPath(image.image, 'images')"
            />
          </div>
          <tooltip
            target="videoOrImage"
          >
          </tooltip>
        </div>
      </div>
    </narrator-bubble>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <narrator-bubble
      v-if="showSpeakerBubble && ['left', 'right', 'extra-left', 'extra-right'].includes(narrative.speaker) && showResponseBubble && narrative.optionalText"
      :left="responseLeft"
      class="narrator-bubble response"
      :class="{
        'with-bars': narrative.showNavbar,
        left: responseLeft,
        native
      }"
      :width="`${(starfighter) ? '850px' : '1400px'}`"
      :style="responseBubbleStyle"
      tooltip-target="responseBubble"
      narrative
      response
    >
      <div
        :style="responseDialogStyle"
      >
        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks,
          but this is okay here since narrativeText is only used/set internally.
          AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
          This is what the following line used to be:
          <paragraphs>{{ narrative.optionalText }}</paragraphs>
        -->
        <p v-html="narrativeOptionalText"></p>
      </div>
    </narrator-bubble>
  </transition>

  <transition
    :enter-active-class="leftCharacterAnimateEnter ? 'animated slideInLeft' : ''"
    :leave-active-class="leftCharacterAnimateLeave ? 'animated slideOutLeft' : ''"
  >
    <player v-if="showLeftCharacters && narrative.leftPlayer"
      :angled="!!(narrative.rightCharacter)"
      :full="!narrative.showNavbar"
      :variant-override="currentLeftVariant"
      :x-offset="currentLeftXOffset"
      :y-offset="currentLeftYOffset"
      tooltip-target="characterLeft"
    ></player>
    <narrator v-else-if="showLeftCharacters && (narrative.leftCharacter || narrative.leftNarrator)"
      left
      :full="!narrative.showNavbar"
      :character="characters[narrative.leftCharacter]"
      :variant-override="currentLeftVariant"
      :x-offset="currentLeftXOffset"
      :y-offset="currentLeftYOffset"
      tooltip-target="characterLeft"
    ></narrator>
  </transition>

  <transition
    :enter-active-class="rightCharacterAnimateEnter ? 'animated slideInRight' : ''"
    :leave-active-class="rightCharacterAnimateLeave ? 'animated slideOutRight' : ''"
  >
    <player v-if="showRightCharacters && narrative.rightPlayer"
      right
      :angled="!!(narrative.leftCharacter)"
      :full="!narrative.showNavbar"
      :variant-override="currentRightVariant"
      :x-offset="currentRightXOffset"
      :y-offset="currentRightYOffset"
      tooltip-target="characterRight"
    ></player>
    <narrator v-else-if="showRightCharacters && (narrative.rightCharacter || narrative.rightNarrator)"
      :full="!narrative.showNavbar"
      :character="characters[narrative.rightCharacter]"
      :variant-override="currentRightVariant"
      :x-offset="currentRightXOffset"
      :y-offset="currentRightYOffset"
      tooltip-target="characterRight"
    ></narrator>
  </transition>

  <transition
    :enter-active-class="extraLeftCharacterAnimateEnter ? 'animated slideInLeft' : ''"
    :leave-active-class="extraLeftCharacterAnimateLeave ? 'animated slideOutLeft' : ''"
  >
    <player v-if="showLeftCharacters && narrative.extraLeftPlayer"
      :angled="!!(narrative.rightCharacter)"
      :full="!narrative.showNavbar"
      :x-offset="currentExtraLeftXOffset"
      :y-offset="currentExtraLeftYOffset"
      :variant-override="currentExtraLeftVariant"
      :style="getNarrativeCharacterStyle('extra-left')"
      tooltip-target="characterExtraLeft"
    ></player>
    <narrator v-else-if="showLeftCharacters && (narrative.extraLeftCharacter || narrative.extraLeftNarrator)"
      left
      :full="!narrative.showNavbar"
      :character="characters[narrative.extraLeftCharacter]"
      :x-offset="currentExtraLeftXOffset"
      :y-offset="currentExtraLeftYOffset"
      :variant-override="currentExtraLeftVariant"
      :style="getNarrativeCharacterStyle('extra-left')"
      tooltip-target="characterExtraLeft"
    >
    </narrator>
  </transition>

  <transition
    :enter-active-class="extraRightCharacterAnimateEnter ? 'animated slideInRight' : ''"
    :leave-active-class="extraRightCharacterAnimateLeave ? 'animated slideOutRight' : ''"
  >
    <player
      v-if="showRightCharacters && narrative.extraRightPlayer"
      right
      :angled="!!(narrative.leftCharacter)"
      :full="!narrative.showNavbar"
      :x-offset="currentExtraRightXOffset"
      :y-offset="currentExtraRightYOffset"
      :variant-override="currentExtraRightVariant"
      :style="getNarrativeCharacterStyle('extra-right')"
      tooltip-target="characterExtraRight"
    ></player>
    <narrator v-else-if="showRightCharacters && (narrative.extraRightCharacter || narrative.extraRightNarrator)"
      :full="!narrative.showNavbar"
      :character="characters[narrative.extraRightCharacter]"
      :x-offset="currentExtraRightXOffset"
      :y-offset="currentExtraRightYOffset"
      :variant-override="currentExtraRightVariant"
      :style="getNarrativeCharacterStyle('extra-right')"
      tooltip-target="characterExtraRight"
    >
    </narrator>
  </transition>

  <bottom-bar
    v-if="!(native && center)"
    :next="((showButtonAndNoFramesToLoad && !center) ? onComplete : undefined)"
    :back="((showButtonAndNoFramesToLoad && !center && enableBack) ? (() => onComplete(true)) : undefined)"
  >
    <span slot="button">
      <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
    </span>
    <span slot="back">
      <span v-html="(narrative && narrative.backButtonText) ? narrative.backButtonText : ((game && game.backButtonDefaultText) ? game.backButtonDefaultText : 'Back')"></span>
    </span>
  </bottom-bar>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <narrator-panel v-if="showSpeakerBubble && center"
      class="narrator-panel"
      :next="onComplete"
      :back="(enableBack ? (() => onComplete(true)) : undefined)"
      :character="narrative.centerNarrator ? narrator : characters[narrative.centerCharacter]"
      :player="narrative.centerPlayer"
    >
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks,
        but this is okay here since narrativeText is only used/set internally.
        AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
        This is what the following line used to be:
        <paragraphs>{{ narrativeText }}</paragraphs>
      -->
      <p v-html="narrativeText"></p>
    </narrator-panel>
  </transition>

  <!-- absolute position fixes z-index popping... go figure -->
  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut"
    style="position: absolute; height: 100%; width: 100%; z-index: 15000;"
  >
    <div
      v-if="showExpandedImage"
      @click.stop="collapseImage"
      style="position: absolute; height: 100%; width: 100%; z-index: 15000;"
    >
      <sliced-box
        style="position: absolute; top: 50px; left: 50px; height: 90%; width: 90%; z-index: 15000;"
      >
        <div
          style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%;"
        >
          <div
            style="position: absolute; top: -60px; right: -20px; font-size: 50px;"
            @click.stop="collapseImage"
            class="btn btn-sm"
            v-focus
          >
            ×
          </div>
          <img
            style="max-width: 100%; height: 80%; object-fit: contain;"
            :src="resolveAssetPath(expandedImage.image, 'images')"
          />
          <p
            v-if="expandedImage.description"
            v-html="expandedImage.description"
            style="font-size: 30px; width: 85%;"
          ></p>
        </div>
      </sliced-box>
    </div>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import Paragraphs from '../../ui/display/Paragraphs';
import TopBar from '../ui/TopBar';
import MetricsNavbar from '../ui/MetricsNavbar';
import CashNavbar from '../ui/CashNavbar';
import BottomBar from '../ui/BottomBar';
import Player from '../ui/Player';
import Narrator from '../ui/Narrator';
import NarratorBubble from '../ui/NarratorBubble';
import NarratorPanel from '../ui/NarratorPanel';
import SlicedBox from '../ui/SlicedBox';
import Tooltip from '../ui/Tooltip';

import { handleIFrames } from '../../../utils/utils';

export default {
  // $emit 'input' on complete
  components: {
    Paragraphs,
    TopBar,
    MetricsNavbar,
    CashNavbar,
    BottomBar,
    Player,
    Narrator,
    NarratorBubble,
    NarratorPanel,
    SlicedBox,
    Tooltip,
  },
  data() {
    return {
      exiting: false,
      framesToLoad: 0,
      showLeftCharacters: false,
      showRightCharacters: false,
      showSpeakerBubble: false,
      showButton: false,
      showName: false,
      showExpandedImage: false,
      expandedImage: null,
      showResponseBubble: false
    };
  },
  computed: {
    ...mapGetters([
      'characters',
      'tooltip',
      'narrative',
      'useMediaZoneForNarrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeText',
      'narrativeOptionalText',
      'narrativeNavbarText',
      'lastProgressionBackwards',
      'leftVariant',
      'leftVariantBeforeBubble',
      'leftCharacterVariantChangeWithBubble',
      'rightVariant',
      'rightVariantBeforeBubble',
      'rightCharacterVariantChangeWithBubble',
      'extraLeftVariant',
      'extraLeftVariantBeforeBubble',
      'extraLeftCharacterVariantChangeWithBubble',
      'extraRightVariant',
      'extraRightVariantBeforeBubble',
      'extraRightCharacterVariantChangeWithBubble',
      'leftCharacterAnimateEnter',
      'leftCharacterAnimateLeave',
      'rightCharacterAnimateEnter',
      'rightCharacterAnimateLeave',
      'extraLeftCharacterAnimateEnter',
      'extraLeftCharacterAnimateLeave',
      'extraRightCharacterAnimateEnter',
      'extraRightCharacterAnimateLeave',
      'narrator',
      'game',
      'starfighter',
      'native'
    ]),
    showButtonAndNoFramesToLoad() {
      return this.showButton && ((this.narrative && this.narrative.noWaitForIFrames) || this.framesToLoad <= 0);
    },
    center() {
      return this.narrative && this.narrative.speaker === 'center';
    },
    enableBack() {
      return (this.narrative.forceEnableBack || (this.game.enableBack && !(this.narrative.preventEnableBack)));
    },
    useVariantBeforeBubble() {
      return !(this.showSpeakerBubble || this.exiting);
    },
    currentLeftVariant() {
      return (this.showResponseBubble && this.narrative.leftExtraVariant)
        ? this.narrative.leftExtraVariant
        : (!(this.useVariantBeforeBubble)
          ? ((this.leftVariant || {}).name || '')
          : ((this.leftVariantBeforeBubble || {}).name || ' ')); // invalid name ' ' for override to take effect when no-variant
    },
    currentLeftXOffset() {
      return (this.leftCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'left' && this.lastNarrative)
        ? (this.lastNarrative.leftXOffset ? this.lastNarrative.leftXOffset : '0px')
        : (this.narrative.leftXOffset ? this.narrative.leftXOffset : '0px');
    },
    currentLeftYOffset() {
      return (this.leftCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'left' && this.lastNarrative)
        ? (this.lastNarrative.leftYOffset ? this.lastNarrative.leftYOffset : '0px')
        : (this.narrative.leftYOffset ? this.narrative.leftYOffset : '0px');
    },
    currentRightVariant() {
      return (this.showResponseBubble && this.narrative.rightExtraVariant)
        ? this.narrative.rightExtraVariant
        : (!(this.useVariantBeforeBubble)
          ? ((this.rightVariant || {}).name || '')
          : ((this.rightVariantBeforeBubble || {}).name || ' ')); // invalid name ' ' for override to take effect when no-variant
    },
    currentRightXOffset() {
      return (this.rightCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'right' && this.lastNarrative)
        ? (this.lastNarrative.rightXOffset ? this.lastNarrative.rightXOffset : '0px')
        : (this.narrative.rightXOffset ? this.narrative.rightXOffset : '0px');
    },
    currentRightYOffset() {
      return (this.rightCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'right' && this.lastNarrative)
        ? (this.lastNarrative.rightYOffset ? this.lastNarrative.rightYOffset : '0px')
        : (this.narrative.rightYOffset ? this.narrative.rightYOffset : '0px');
    },
    currentExtraLeftVariant() {
      return (this.showResponseBubble && this.narrative.extraLeftExtraVariant)
        ? this.narrative.extraLeftExtraVariant
        : (!(this.useVariantBeforeBubble)
          ? ((this.extraLeftVariant || {}).name || ' ')
          : ((this.extraLeftVariantBeforeBubble || {}).name || ' ')); // invalid name ' ' for override to take effect when no-variant
    },
    currentExtraLeftXOffset() {
      return (this.extraLeftCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'extra-left' && this.lastNarrative)
        ? (this.lastNarrative.extraLeftXOffset ? this.lastNarrative.extraLeftXOffset : '0px')
        : (this.narrative.extraLeftXOffset ? this.narrative.extraLeftXOffset : '0px');
    },
    currentExtraLeftYOffset() {
      return (this.extraLeftCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'extra-left' && this.lastNarrative)
        ? (this.lastNarrative.extraLeftYOffset ? this.lastNarrative.extraLeftYOffset : '0px')
        : (this.narrative.extraLeftYOffset ? this.narrative.extraLeftYOffset : '0px');
    },
    currentExtraRightVariant() {
      return (this.showResponseBubble && this.narrative.extraRightExtraVariant)
        ? this.narrative.extraRightExtraVariant
        : (!(this.useVariantBeforeBubble)
          ? ((this.extraRightVariant || {}).name || ' ')
          : ((this.extraRightVariantBeforeBubble || {}).name || ' ')); // invalid name ' ' for override to take effect when no-variant
    },
    currentExtraRightXOffset() {
      return (this.extraRightCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'extra-right' && this.lastNarrative)
        ? (this.lastNarrative.extraRightXOffset ? this.lastNarrative.extraRightXOffset : '0px')
        : (this.narrative.extraRightXOffset ? this.narrative.extraRightXOffset : '0px');
    },
    currentExtraRightYOffset() {
      return (this.extraRightCharacterVariantChangeWithBubble && this.useVariantBeforeBubble
        && this.narrative.speaker === 'extra-right' && this.lastNarrative)
        ? (this.lastNarrative.extraRightYOffset ? this.lastNarrative.extraRightYOffset : '0px')
        : (this.narrative.extraRightYOffset ? this.narrative.extraRightYOffset : '0px');
    },
    bubbleVariant() {
      if (this.narrative.speaker === 'left') {
        return this.leftVariant;
      } else if (this.narrative.speaker === 'right') {
        return this.rightVariant;
      } else if (this.narrative.speaker === 'extra-left') {
        return this.extraLeftVariant;
      } else if (this.narrative.speaker === 'extra-right') {
        return this.extraRightVariant;
      }
      return null;
    },
    responseVariant() {
      if (this.narrative.responder === 'left') {
        return this.leftVariant;
      } else if (this.narrative.responder === 'right') {
        return this.rightVariant;
      } else if (this.narrative.responder === 'extra-left') {
        return this.extraLeftVariant;
      } else if (this.narrative.responder === 'extra-right') {
        return this.extraRightVariant;
      }
      return null;
    },
    bubbleLeft() {
      return (this.narrative.speaker === 'left' || this.narrative.speaker === 'extra-left');
    },
    responseLeft() {
      return this.narrative.responder === 'left'
        || this.narrative.reponder === 'extra-left'
        || (this.narrative.reponder == null
          && (this.narrative.speaker === 'right' || this.narrative.speaker === 'extra-right'));
    },
    bubbleStyle() {
      // hardcoded defaults based on theme
      let topVal =
        ((this.native) ? '80px' : ((this.narrative.showNavbar) ? '150px' : '180px'));
      let leftVal = '380px';
      let rightVal;
      if (this.native) {
        if (this.bubbleLeft) {
          leftVal = undefined;
          rightVal = '420px';
        } else {
          leftVal = '420px';
        }
      } else if (this.bubbleLeft) {
        leftVal = undefined;
        rightVal = '400px';
      }
      let totalYOffset = '';
      let totalXOffset = '';
      // offset from image variant setting
      if (this.bubbleVariant != null) {
        if (this.bubbleVariant.speechYOffset) {
          totalYOffset += ` - ${this.bubbleVariant.speechYOffset}`;
        }
        if (this.bubbleVariant.speechXOffset) {
          totalXOffset += ` - ${this.bubbleVariant.speechXOffset}`;
        }
      }
      // offset from narrative extra character setting
      const ncOffsets = this.getNarrativeCharacterOffsets(this.narrative.speaker);
      if (ncOffsets != null) {
        if (ncOffsets.y) {
          totalYOffset += ` - ${ncOffsets.y}`;
        }
        if (ncOffsets.x) {
          totalXOffset += ` - ${ncOffsets.x}`;
        }
      }
      // offset from narrative offset setting
      if (this.narrative.bubbleYOffset) {
        totalYOffset += ` - ${this.narrative.bubbleYOffset}`;
      }
      if (this.narrative.bubbleXOffset) {
        totalXOffset += ` - ${this.narrative.bubbleXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        leftVal = leftVal ? `calc(${leftVal}${totalXOffset})` : undefined;
        rightVal = rightVal ? `calc(${rightVal}${totalXOffset})` : undefined;
      }
      return {
        top: topVal,
        left: leftVal,
        right: rightVal
      };
    },
    responseBubbleStyle() {
      // hardcoded defaults based on theme
      let topVal = '425px';
      let leftVal = '380px';
      let rightVal;
      if (this.native) {
        if (this.responseLeft) {
          leftVal = undefined;
          rightVal = '420px';
        } else {
          leftVal = '420px';
        }
      } else if (this.responseLeft) {
        leftVal = undefined;
        rightVal = '400px';
      }
      let totalYOffset = '';
      let totalXOffset = '';
      // offset from image variant setting
      if (this.responseVariant != null) {
        if (this.responseVariant.speechYOffset) {
          totalYOffset += ` - ${this.responseVariant.speechYOffset}`;
        }
        if (this.responseVariant.speechXOffset) {
          totalXOffset += ` - ${this.responseVariant.speechXOffset}`;
        }
      }
      // offset from narrative extra character setting
      const ncOffsets = this.getNarrativeCharacterOffsets(this.narrative.responder);
      if (ncOffsets != null) {
        if (ncOffsets.y) {
          totalYOffset += ` - ${ncOffsets.y}`;
        }
        if (ncOffsets.x) {
          totalXOffset += ` - ${ncOffsets.x}`;
        }
      }
      // offset from narrative response setting
      if (this.narrative.responseYOffset) {
        totalYOffset += ` - ${this.narrative.responseYOffset}`;
      }
      if (this.narrative.responseXOffset) {
        totalXOffset += ` - ${this.narrative.responseXOffset}`;
      }
      // output final position style
      if (totalYOffset) {
        topVal = `calc(${topVal}${totalYOffset})`;
      }
      if (totalXOffset) {
        leftVal = leftVal ? `calc(${leftVal}${totalXOffset})` : undefined;
        rightVal = rightVal ? `calc(${rightVal}${totalXOffset})` : undefined;
      }
      return {
        top: topVal,
        left: leftVal,
        right: rightVal
      };
    },
    dialogStyle() {
      if (this.native) {
        if (this.narrative.speaker !== 'left' && this.narrative.speaker !== 'extra-left') {
          return {
            margin: '-54px 620px -32px -18px',
          };
        }
        return {
          margin: '-54px -18px -32px 620px',
        };
      }
      return '';
    },
    responseDialogStyle() {
      if (this.native) {
        if (this.narrative.speaker === 'left' || this.narrative.speaker === 'extra-left') {
          return {
            margin: '-54px 620px -32px -18px',
          };
        }
        return {
          margin: '-54px -18px -32px 620px',
        };
      }
      return '';
    }
  },
  methods: {
    ...mapActions([
      'stopSounds',
      'rewindToTooltipTarget',
      'playSound',
      'playVoice',
    ]),
    async show() {
      this.framesToLoad = 0;
      this.exiting = false;
      this.showLeftCharacters = true;
      this.showRightCharacters = true;
      let hasStoppedSounds = false;
      if (!(this.lastProgressionBackwards)) {
        if (!hasStoppedSounds) {
          await Vue.nextTick();
          this.stopSounds();
          hasStoppedSounds = true;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      this.showName = true;
      this.showSpeakerBubble = true;
      if (!hasStoppedSounds) {
        await Vue.nextTick();
        this.stopSounds();
        hasStoppedSounds = true;
      }
      if (this.narrative.textSound) {
        await Vue.nextTick();
        this.playSound({ sound: this.narrative.textSound });
      }
      if (this.narrative.textVoiceLine) {
        await Vue.nextTick();
        this.playVoice({ line: this.narrative.textVoiceLine });
      }
      await Vue.nextTick();
      handleIFrames(this);
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showButton = true;
    },
    async hide() {
      this.exiting = true;
      this.showSpeakerBubble = false;
      this.showResponseBubble = false;
      this.showButton = false;
      this.showLeftCharacters = !this.leftCharacterAnimateLeave;
      this.showRightCharacters = !this.rightCharacterAnimateLeave;
      this.showName = false;
      if (this.leftCharacterAnimateLeave || this.rightCharacterAnimateLeave) {
        await new Promise(resolve => setTimeout(resolve, 285));
      } else {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      this.stopSounds();
    },
    // call for back to back segments of this type
    async reset() {
      let hasStoppedSounds = false;
      this.framesToLoad = 0;
      this.showResponseBubble = false;
      this.showButton = false;
      this.exiting = false;
      if (this.lastProgressionBackwards) {
        this.showLeftCharacters = true;
        this.showRightCharacters = true;
        this.showSpeakerBubble = true;
      } else {
        this.showLeftCharacters = !this.leftCharacterAnimateEnter;
        this.showRightCharacters = !this.rightCharacterAnimateEnter;
        this.showSpeakerBubble = false;
        if (!hasStoppedSounds) {
          await Vue.nextTick();
          this.stopSounds();
          hasStoppedSounds = true;
        }
        if (this.leftCharacterAnimateEnter || this.rightCharacterAnimateEnter) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          this.showLeftCharacters = true;
          this.showRightCharacters = true;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.showSpeakerBubble = true;
      }
      if (!hasStoppedSounds) {
        await Vue.nextTick();
        this.stopSounds();
        hasStoppedSounds = true;
      }
      if (this.narrative.textSound) {
        await Vue.nextTick();
        this.playSound({ sound: this.narrative.textSound });
      }
      if (this.narrative.textVoiceLine) {
        await Vue.nextTick();
        this.playVoice({ line: this.narrative.textVoiceLine });
      }
      await Vue.nextTick();
      handleIFrames(this);
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.showButton = true;
    },
    async onComplete(back = false) {
      if (this.narrative.type === 'dialog' && this.narrative.optionalText
        && ((!(this.showResponseBubble) && !back) || (this.showResponseBubble && back))) {
        this.showResponseBubble = !(this.showResponseBubble);
        if (this.showResponseBubble && this.narrative.optionalTextVoiceLine) {
          await Vue.nextTick();
          this.playVoice({ line: this.narrative.optionalTextVoiceLine });
        }
        if (back) {
          await new Promise(resolve => setTimeout(resolve, 475)); // wait for tooltip exit delay
          await this.rewindToTooltipTarget({ target: 'responseBubble', tooltips: this.narrative.tooltips || [] });
        }
        if (this.tooltip) window.dispatchEvent(new Event('click'));
        await Vue.nextTick();
        handleIFrames(this);
      } else {
        if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
          window.dispatchEvent(new Event('click'));
          return;
        }
        if (!this.nextNarrative) await this.hide();
        this.showButton = false;
        await Vue.nextTick();
        this.$emit('input', back);
      }
    },
    expandImage(image) {
      this.expandedImage = image;
      this.showExpandedImage = true;
    },
    collapseImage(e) {
      if (e.target === e.currentTarget) {
        this.showExpandedImage = false;
      }
    },
    getNarrativeCharacterStyle(position) {
      return {
        'z-index': this.getNarrativeCharacterOrder(position)
      };
    },
    getNarrativeCharacterOrder(position) {
      if (position === 'left') {
        return this.narrative.leftOrderOverride ? this.narrative.leftOrderOverride : 0;
      } else if (position === 'right') {
        return this.narrative.rightOrderOverride ? this.narrative.rightOrderOverride : 0;
      } else if (position === 'extra-left') {
        return this.narrative.extraLeftOrderOverride ? this.narrative.extraLeftOrderOverride : 0;
      } else if (position === 'extra-right') {
        return this.narrative.extraRightOrderOverride ? this.narrative.extraRightOrderOverride : 0;
      }
      return 0;
    },
    getNarrativeCharacterOffsets(position) {
      if (position === 'left') {
        return {
          x: this.narrative.leftXOffset,
          y: this.narrative.leftYOffset
        };
      } else if (position === 'right') {
        return {
          x: this.narrative.rightXOffset,
          y: this.narrative.rightYOffset
        };
      } else if (position === 'extra-left') {
        return {
          x: this.narrative.extraLeftXOffset,
          y: this.narrative.extraLeftYOffset
        };
      } else if (position === 'extra-right') {
        return {
          x: this.narrative.extraRightXOffset,
          y: this.narrative.extraRightYOffset
        };
      }
      return null;
    }
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    }
  },
  created() {
    this.showLeftCharacters = !this.leftCharacterAnimateEnter;
    this.showRightCharacters = !this.rightCharacterAnimateEnter;
  },
  async mounted() {
    this.showButton = false;
    await this.show();
  },
  async beforeDestroy() {
    await this.hide();
  }
};
</script>

<style lang="scss" scoped>
// .narrator-bubble {
//   top: 180px;
//   left: 380px;
// }
// .narrator-bubble.with-bars {
//   top: 150px;
// }
// .narrator-bubble.native {
//   top: 80px;
//   left: 420px;
// }
// .narrator-bubble.response {
//   top: 425px;
// }
// .narrator-bubble.native.left {
//   left: unset;
//   right: 420px;
// }
// .narrator-bubble.left {
//   left: unset;
//   right: 400px;
// }
.narrator-panel {
  left: 30px;
  bottom: 70px;
}
.narrative-image:hover {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeDialog.vue